import { FC, Fragment, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetIncidentDetailQuery, useGetIncidentLayoutQuery } from "../../../api/network/incidentApiServices";
import { downloadEventVideo, useGetEventVideoDownloadUrlMutation } from "../../../api/network/vmApiService";
import HeaderView from "../../../components/admin/headerView";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import svgIcons from "../../../assets/svg/svgIcons";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import { dbTimeToJsTime, formattedDateTimeBasedOnTimezone } from "../../../utils/dateTime";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import IncidentDetailDispositionBar from "./incident-detail-disposition-bar";
import IncidentDetailEventDetails from "./incident-detail-event-details";
import IncidentDetailEventReport from "./incident-detail-event-report";
import IncidentDetailGpsMap from "./incident-detail-gps-map";
import IncidentDetailVideo from "./incident-detail-video";
import IncidentDetailWeather from "./incident-detail-weather";
import { INCIDENT_DISPOSITIONS, IncidentDetailResponse, TIncidentWidgetId } from "./type";

const WIDGET_COMPONENTS: Record<
  TIncidentWidgetId,
  FC<any>
> = {
  video: IncidentDetailVideo,
  gps_map: IncidentDetailGpsMap,
  weather: IncidentDetailWeather,
  event_details: IncidentDetailEventDetails,
};

const HeaderMetaPair: FC<{ label: string; value: string }> = ({ label, value }) => (
  <span className="whitespace-nowrap">
    <span className="font-semibold text-heading-black">{label}: </span>
    <span className="text-[#64748B] font-normal">{value}</span>
  </span>
);

const ScreenIncidentDetail: FC = () => {
  const { recordid } = useParams<{ recordid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.incident.incident_review.detailsPage",
  });
  const { convertToTZForDateFns, tzForDateFns, tzForDisplay } = useAccountTimezone();

  const thisOrgIncidentReviewAllowed = useLoggedInUserData("incidentreviewallowed");
  const { getUserACLAccessLevel } = useACL();
  useEffect(() => {
    if (
      thisOrgIncidentReviewAllowed !== "1" ||
      getUserACLAccessLevel(ACL_NAMES.ACL_INCIDENT_REVIEW) < ROLE_ACCESS_LEVELS.READ
    ) {
      navigate(routeUrls.dashboard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: dataLayout, isFetching: isFetchingLayout } = useGetIncidentLayoutQuery();
  const {
    data: dataDetail,
    isFetching: isFetchingDetail,
    isError: isErrorDetail,
  } = useGetIncidentDetailQuery(recordid || "", {
    skip: !recordid,
  });
  // held locally so the disposition PUT response can update the page without a refetch
  const [incident, setIncident] = useState<IncidentDetailResponse | null>(null);
  const [printMode, setPrintMode] = useState(false);
  const [loadedWidgets, setLoadedWidgets] = useState<Set<TIncidentWidgetId>>(new Set());
  const [temperatureUnit, setTemperatureUnit] = useState<"C" | "F">("F");

  useEffect(() => {
    if (dataDetail) setIncident(dataDetail);
  }, [dataDetail]);

  const handleTabLoadComplete = useCallback((id: any) => {
    setLoadedWidgets(prev => {
      if (prev.has(id)) {
        return prev; // No state update
      }
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
  if (!printMode || loadedWidgets.size < 4) {
    return;
  }
  const timer = window.setTimeout(() => {
        window.print();
  }, 100);
  return () => clearTimeout(timer);
}, [printMode, loadedWidgets]);

useEffect(() => {
  const handleAfterPrint = () => {
    setPrintMode(false);
  };
  window.addEventListener("afterprint", handleAfterPrint);
  return () => {
    window.removeEventListener("afterprint", handleAfterPrint);
  };
}, []);

  const onBackButtonClick = () => navigate(routeUrls.dashboardChildren.incident_review);

  const [getVideoDownloadUrl, { isLoading: isDownloadingVideo }] = useGetEventVideoDownloadUrlMutation();
  const handleDownloadVideo = () => {
    if (!incident?.video_ticket) {
      toast.error(t("download_video_error"));
      return;
    }
    const fileName = `vid_${incident.recordid}`;
    getVideoDownloadUrl({ ticketId: incident.video_ticket, fileName })
      .unwrap()
      .then((res) => {
        if (res?.download_url) {
          downloadEventVideo(res.download_url, fileName, undefined, () => toast.error(t("download_video_error")));
        } else {
          toast.error(t("download_video_error"));
        }
      })
      .catch(() => toast.error(t("download_video_error")));
  };

  if (isErrorDetail) {
    return (
      <>
        <HeaderView title={t("heading")} showBackButton={true} backButtonCallback={onBackButtonClick} />
        <div className="flex items-center justify-center h-64">
          <p className="text-heading-gray666 text-sm">{t("fetch_error")}</p>
        </div>
      </>
    );
  }

  if (isFetchingLayout || isFetchingDetail || !incident) {
    return (
      <>
        <HeaderView title={t("heading")} showBackButton={true} backButtonCallback={onBackButtonClick} />
        <div className="flex items-center justify-center h-64">
          <img src={svgIcons.LoadingAnimation} alt="" className="size-12" />
        </div>
      </>
    );
  }

  // first 3 visible widgets fill the top row, rest fall to the bottom row
  const visibleWidgets = (dataLayout?.widgets || []).filter((w) => w.visible);
  const topRowWidgets = visibleWidgets.slice(0, 3);
  const bottomRowWidgets = visibleWidgets.slice(3);

  return (
    <Fragment key={incident.recordid}>
      <div className="no-print">
        <HeaderView
          title={
            // custom back-arrow instead of HeaderView's built-in one, since that
            // one centers against the full 3-line title block
            <div className="flex items-start gap-1">
              <img
                src={svgIcons.LeftArrowIcon}
                alt="left-arrow-icon"
                className="p-2 bg-accent-blue-pale rounded-full cursor-pointer shrink-0"
                onClick={onBackButtonClick}
              />
              <div>
                <div className="text-2xl md:text-[28px] leading-tight font-bold tracking-wide text-accent-blue-dark">
                  {`INC-${incident.recordid}`}
                </div>
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] leading-4 mt-1.5">
                  <HeaderMetaPair label={t("header_event_type")} value={incident.incident_type} />
                  <span className="text-heading-gray888/60">|</span>
                  <HeaderMetaPair
                    label={t("header_date_time")}
                    value={`${formattedDateTimeBasedOnTimezone(
                      dbTimeToJsTime(incident.timestamp),
                      convertToTZForDateFns(tzForDateFns),
                      "LLLL dd, yyyy hh:mmaa"
                    )} ${tzForDisplay}`}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] leading-4 mt-0.5">
                  <HeaderMetaPair
                    label={t("header_vehicle")}
                    value={`${incident.asset_name} (${incident.deviceid})`}
                  />
                  <span className="text-heading-gray888/60">|</span>
                  <HeaderMetaPair label={t("header_driver")} value={incident.driver_name || "-"} />
                </div>
              </div>
            </div>
          }
          showBackButton={false}
          rightComponent={
            <div className="flex items-start gap-2.5">
              <IncidentDetailDispositionBar
                incident={incident}
                onDispositionChanged={(updated) =>
                  setIncident((prev) => (prev ? { ...prev, ...updated } : prev))
                }
              />
              <AdminFormFieldSubmit
                type="button"
                variant="secondary"
                label={isDownloadingVideo ? t("downloading_video") : t("download_video")}
                disabled={!incident.has_video || isDownloadingVideo}
                onClick={handleDownloadVideo}
                customWrapperClass=""
                buttonDimensionClass="h-9 px-4 whitespace-nowrap"
              />
              <AdminFormFieldSubmit
                type="button"
                variant="secondary"
                label={
                  <span className="flex items-center gap-2">
                    {t("export_incident_report")}
                    {printMode ?
                      <img src={svgIcons.LoadingAnimation} alt="" className="size-6" />
                      :
                      <img src={svgIcons.Download} alt="" className="size-4" />
                    }
                  </span>
                }
                disabled={printMode}
                customWrapperClass=""
                buttonDimensionClass="h-9 px-4 whitespace-nowrap"
                onClick={() => {
                  setLoadedWidgets(new Set()); // clear before starting a new print pass
                  setPrintMode(true);
                }}
              />
            </div>
          }
        />
        <div className="no-print flex-1 overflow-y-auto bg-[#F7F8FA]">
          <div className="flex flex-col gap-5 p-4 md:p-5">
            {topRowWidgets.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {topRowWidgets.map((widget) => {
                  const WidgetComponent = WIDGET_COMPONENTS[widget.id];
                  if (!WidgetComponent) return null;
                  {
                    return widget.id === "weather" ?
                    <WidgetComponent  key={widget.id} incident={incident} temperatureUnit={temperatureUnit} onTemperatureUnitChange={setTemperatureUnit} />
                    :
                    <WidgetComponent  key={widget.id} incident={incident} />
                  }
                })}
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {bottomRowWidgets.map((widget) => {
                const WidgetComponent = WIDGET_COMPONENTS[widget.id];
                if (!WidgetComponent) return null;
                return <WidgetComponent key={widget.id} incident={incident} />;
              })}
              <IncidentDetailEventReport incident={incident} />
            </div>
          </div>
        </div>
        </div>
        {printMode &&
          <div className="print-incident">
            <div className="text-2xl leading-tight font-bold tracking-wide">
              {`INC-${incident.recordid}`}
            </div>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 text-md leading-4 my-4">
              <HeaderMetaPair label={t("header_event_type")} value={incident.incident_type} />
              <span className="text-heading-gray888/60">|</span>
              <HeaderMetaPair
                label={t("header_date_time")}
                value={`${formattedDateTimeBasedOnTimezone(
                  dbTimeToJsTime(incident.timestamp),
                  convertToTZForDateFns(tzForDateFns),
                  "LLLL dd, yyyy hh:mmaa"
                )} ${tzForDisplay}`}
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 text-md leading-4 mb-4">
              <HeaderMetaPair
                label={t("header_vehicle")}
                value={`${incident.asset_name} (${incident.deviceid})`}
              />
              <span className="text-heading-gray888/60">|</span>
              <HeaderMetaPair label={t("header_driver")} value={incident.driver_name || "-"} />
            </div>
            <div className="flex flex-col items-start gap-y-4 text-md leading-4 my-4">
              <HeaderMetaPair
                label={t("export_incident_print.dispostion")}
                value={INCIDENT_DISPOSITIONS[incident.disposition]?.label || "-"}
              />
              <HeaderMetaPair
                label={t("export_incident_print.dispostion_note")}
                value={incident.disposition_comment || "-"}
              />
            </div>
            {visibleWidgets.map((widget) => {
              const WidgetComponent = WIDGET_COMPONENTS[widget.id];
              if (!WidgetComponent) return null;
              return (
                widget.id === "weather" ?
                  <WidgetComponent incident={incident} printMode={true} onLoadComplete={() => handleTabLoadComplete(widget)} temperatureUnit={temperatureUnit} />
                  :
                  <WidgetComponent incident={incident} printMode={true} onLoadComplete={() => handleTabLoadComplete(widget)} />
              )
            })
            }
            <IncidentDetailEventReport incident={incident}  printMode={true}/>
          </div>
        }
    </Fragment>
  );
};

export default ScreenIncidentDetail;
